// useBookingForm — form state for the multi-vehicle book-in flow.
//
// Owns:
//   - The booking-level fields (date, timeframe, driver, source,
//     payment_mode, collection address, notes)
//   - The vehicle list (1–3 entries, each VehicleFormValue)
//   - The customer panel — either a selected CustomerSummary OR the
//     fields the user typed for a brand-new customer
//   - Per-vehicle duplicate-registration warnings
//   - Auto-save drafts every 30 seconds (server `/drafts` endpoint;
//     sessionStorage fallback when the server is unreachable so users
//     never lose data on refresh)
//   - The submit mutation
//
// Drafts: Each tab gets a single 'live' draft keyed by a per-session
// drafts UUID kept in sessionStorage. We round-trip `draft_type='booking'`
// + `data: <serialised form>` through GET/POST/PUT /api/v1/collections/drafts. On
// successful submit the draft is deleted.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { createCustomer } from '../../../api/customers';
import { useToast } from '../../../hooks/useToast';
import { useDraftManager } from '../../../hooks/useDraftManager';
import type {
  BookingDetailResponse,
  BookingSummary,
  CreateBookingInput,
  CreateBookingResponse,
  CustomerSummary,
  DuplicateBookingMatch,
  DuplicateBookingResponse,
  Timeframe,
} from '../../../types/api';
import type { VehicleFormValue } from '../components/VehicleForm';

export interface CustomerPanel {
  /** When set: the parent linked an existing customer. */
  customer: CustomerSummary | null;
  newMode: boolean;
  fields: {
    name: string;
    email: string;
    phone: string;
    phone2: string;
    address: string;
    postcode: string;
  };
}

export interface BookingFormState {
  date: string; // YYYY-MM-DD
  timeframe: Timeframe;
  driver: number | null;
  source: number | null;
  payment_mode: number | null;
  collection_address: string;
  collection_postcode: string;
  use_customer_address: boolean;
  notes: string;
  customer: CustomerPanel;
  vehicles: VehicleFormValue[];
  send_email: boolean;
  send_sms: boolean;
  reminder_email: boolean;
  reminder_sms: boolean;
}

export const EMPTY_VEHICLE: VehicleFormValue = {
  car_make_model: '',
  car_registration: '',
  product_id: null,
  price: '',
  notes: '',
};

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function makeInitialState(): BookingFormState {
  return {
    date: todayIso(),
    timeframe: 'AM',
    driver: null,
    source: null,
    payment_mode: null,
    collection_address: '',
    collection_postcode: '',
    use_customer_address: true,
    notes: '',
    customer: {
      customer: null,
      newMode: false,
      fields: {
        name: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        postcode: '',
      },
    },
    vehicles: [{ ...EMPTY_VEHICLE }],
    send_email: true,
    send_sms: true,
    reminder_email: true,
    reminder_sms: false,
  };
}

function draftDataToState(data: unknown): BookingFormState | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Partial<BookingFormState>;
  if (!Array.isArray(candidate.vehicles)) return null;
  const initial = makeInitialState();
  return {
    ...initial,
    ...candidate,
    customer: {
      ...initial.customer,
      ...(candidate.customer ?? {}),
      fields: {
        ...initial.customer.fields,
        ...(candidate.customer?.fields ?? {}),
      },
    },
    vehicles: candidate.vehicles.map((v) => ({ ...EMPTY_VEHICLE, ...v })),
  };
}

function buildSubmitPayload(state: BookingFormState): CreateBookingInput {
  const cp = state.customer;
  const linked = cp.customer;
  const name = linked ? linked.full_name : cp.fields.name;
  const phone = linked ? linked.phone : cp.fields.phone;
  const email = (linked ? linked.email : cp.fields.email) || null;
  const phone2 = (linked ? linked.phone2 : cp.fields.phone2) || null;
  const address = linked ? linked.address : cp.fields.address;
  const postcode = linked ? linked.postcode : cp.fields.postcode;

  const collection_address = state.use_customer_address
    ? null
    : state.collection_address || null;
  const collection_postcode = state.use_customer_address
    ? null
    : state.collection_postcode || null;

  return {
    customer_id: linked ? linked.id : null,
    customer_uuid: linked ? linked.uuid : null,
    name,
    email,
    phone,
    phone2,
    address,
    postcode,
    collection_address,
    collection_postcode,
    date: state.date,
    timeframe: state.timeframe,
    driver: state.driver,
    source: state.source,
    payment_mode: state.payment_mode,
    vehicles: state.vehicles.map((v) => {
      // Per-vehicle notes fall back to the booking-level Notes card
      // (state.notes) when blank or whitespace-only. With a single
      // vehicle the per-vehicle textarea is hidden, so the fallback
      // is how the user's typed value reaches the row at all.
      const vehicleNote = v.notes.trim();
      const generalNote = state.notes.trim();
      const resolvedNote = vehicleNote || generalNote;
      return {
        car_make_model: v.car_make_model.trim(),
        car_registration: v.car_registration.trim(),
        product_id: v.product_id,
        price: Number.parseFloat(v.price || '0') || 0,
        notes: resolvedNote || null,
      };
    }),
    send_email: state.send_email,
    send_sms: state.send_sms,
    reminder_email: state.reminder_email,
    reminder_sms: state.reminder_sms,
  };
}

export function isStatePopulated(state: BookingFormState): boolean {
  if (state.customer.customer) return true;
  const f = state.customer.fields;
  if (f.name.trim() || f.phone.trim() || f.address.trim()) return true;
  return state.vehicles.some(
    (v) => v.car_registration.trim() || v.car_make_model.trim(),
  );
}

// ---- Draft title -----------------------------------------------------------

// The stored title doubles as the Draft Manager preview line, so surface the
// customer name when we have one (falls back to a generic label).
function bookingDraftTitle(state: BookingFormState): string {
  const name =
    state.customer.customer?.full_name?.trim() ||
    state.customer.fields.name.trim();
  return name ? `Booking — ${name}` : 'Booking draft';
}

// ---- Hook ------------------------------------------------------------------

export interface UseBookingFormOptions {
  /** When provided, the hook seeds the form from an existing booking row
   *  (BookingEditPage). Drafts are NOT touched in edit mode. */
  initialBooking?: BookingDetailResponse | null;
  /** Callback fired when a successful submit returns. */
  onSubmitSuccess?: (response: CreateBookingResponse) => void;
}

export function useBookingForm(opts: UseBookingFormOptions = {}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(opts.initialBooking);

  const [state, setState] = useState<BookingFormState>(() => makeInitialState());
  const [editSeeded, setEditSeeded] = useState(false);
  const [duplicates, setDuplicates] = useState<
    Record<number, DuplicateBookingMatch | null>
  >({});

  // Draft persistence (auto-save, auto-resume, manual save/load/delete/list)
  // is owned by the shared useDraftManager hook. Dormant in edit mode so an
  // existing booking is never round-tripped through the drafts store.
  const draftManager = useDraftManager<BookingFormState>({
    draftType: 'booking',
    formData: state,
    onLoad: setState,
    isPopulated: isStatePopulated,
    deserialize: draftDataToState,
    getTitle: bookingDraftTitle,
    enabled: !isEdit,
  });

  // Edit mode — seed from the supplied booking once.
  useEffect(() => {
    if (!opts.initialBooking || editSeeded) return;
    const b = opts.initialBooking.booking;
    const related = opts.initialBooking.related_vehicles;
    const allVehicles: BookingSummary[] = [b, ...related];
    setState((prev) => ({
      ...prev,
      date: b.date,
      timeframe: b.timeframe,
      driver: b.driver_id,
      source: b.source_id,
      payment_mode: b.payment_mode_id,
      use_customer_address: !b.collection_address,
      collection_address: b.collection_address ?? '',
      collection_postcode: b.collection_postcode ?? '',
      notes: b.notes ?? '',
      customer: {
        customer: null,
        newMode: false,
        fields: {
          // Same normalised-null guard as setCustomer: contact fields can be
          // null on the booking's customer snapshot post-normalisation.
          name: b.name ?? '',
          email: b.email ?? '',
          phone: b.phone ?? '',
          phone2: b.phone2 ?? '',
          address: b.address ?? '',
          postcode: b.postcode ?? '',
        },
      },
      vehicles: allVehicles.slice(0, 3).map((v) => ({
        car_make_model: v.car_make_model,
        car_registration: v.car_registration,
        product_id: v.product_id,
        price: v.price.toFixed(2),
        notes: v.notes ?? '',
      })),
      send_email: false,
      send_sms: false,
      reminder_email: b.reminder_email ?? true,
      reminder_sms: b.reminder_sms ?? false,
    }));
    setEditSeeded(true);
  }, [opts.initialBooking, editSeeded]);

  // ---- Duplicate-reg blur check ------------------------------------------

  // Tracks whether to surface the modal flow for a given vehicle index.
  const [duplicateModal, setDuplicateModal] = useState<{
    index: number;
    registration: string;
    bookings: DuplicateBookingMatch[];
    pending: DuplicateBookingMatch[];
  } | null>(null);

  const checkDuplicate = useCallback(
    async (index: number, registration: string) => {
      if (!registration) {
        setDuplicates((prev) => ({ ...prev, [index]: null }));
        return;
      }
      try {
        const res = await apiClient.get<DuplicateBookingResponse>(
          endpoints.bookings.checkDuplicate,
          { params: { reg: registration } },
        );
        const dups = res.data?.duplicates ?? { bookings: [], pending: [] };
        const bookingMatches = dups.bookings ?? [];
        const pendingMatches = dups.pending ?? [];

        // Inline single-line warning — keep it for the existing legacy UX.
        // Pick the most-recent booking match (server orders by date desc).
        const firstBooking = bookingMatches[0] ?? null;
        setDuplicates((prev) => ({ ...prev, [index]: firstBooking }));

        // Modal flow — open when ANY match exists.
        if (bookingMatches.length > 0 || pendingMatches.length > 0) {
          setDuplicateModal({
            index,
            registration,
            bookings: bookingMatches,
            pending: pendingMatches,
          });
        }
      } catch {
        setDuplicates((prev) => ({ ...prev, [index]: null }));
      }
    },
    [],
  );

  // Staff chose "Continue Anyway" on the duplicate modal, i.e. they have seen
  // the existing records. Sent as confirm_duplicate so the server's same-day
  // guard (a confirmable warning, as in legacy) doesn't ask a second time.
  // The acknowledgement is scoped to the registrations staff actually saw
  // (normalised plates). confirm_duplicate is only sent while EVERY vehicle's
  // plate is in the set, so editing a plate or adding a new vehicle re-asks
  // instead of silently riding on an earlier "Continue Anyway".
  const duplicateAckRef = useRef<Set<string>>(new Set());
  const normReg = (r: string): string => r.replace(/\s+/g, '').toUpperCase();
  const [duplicateConfirm, setDuplicateConfirm] = useState<{ message: string } | null>(null);

  const dismissDuplicateModal = useCallback(() => {
    if (duplicateModal !== null) {
      duplicateAckRef.current.add(normReg(duplicateModal.registration));
    }
    setDuplicateModal(null);
  }, [duplicateModal]);

  const clearVehicleRegistration = useCallback((index: number) => {
    setState((prev) => {
      const next = [...prev.vehicles];
      const v = next[index];
      if (v) next[index] = { ...v, car_registration: '' };
      return { ...prev, vehicles: next };
    });
    setDuplicates((prev) => ({ ...prev, [index]: null }));
    setDuplicateModal(null);
    duplicateAckRef.current.clear();
  }, []);

  const duplicateWarnings = useMemo(() => {
    const out: Record<number, string | null> = {};
    state.vehicles.forEach((_, i) => {
      const m = duplicates[i];
      if (!m || !m.date) {
        out[i] = null;
        return;
      }
      const dateLabel = new Date(`${m.date}T00:00:00`).toLocaleDateString(
        undefined,
        { day: '2-digit', month: 'short', year: 'numeric' },
      );
      out[i] = `This registration was booked on ${dateLabel} (${m.reference}). Check before proceeding.`;
    });
    return out;
  }, [duplicates, state.vehicles]);

  // ---- Submit ------------------------------------------------------------

  const submitMutation = useMutation({
    mutationFn: async () => {
      let payload = buildSubmitPayload(state);
      const plates = state.vehicles
        .map((v) => normReg(v.car_registration))
        .filter((r) => r.length > 0);
      if (plates.length > 0 && plates.every((r) => duplicateAckRef.current.has(r))) {
        payload = { ...payload, confirm_duplicate: true };
      }

      // If no linked customer was selected from autocomplete, auto-create one
      // first and inject the returned id/uuid into the payload before submitting
      // the booking. This mirrors the legacy book_in.php flow where a new
      // customer name/phone entered in the form is inserted into the customers
      // table before the booking row is written.
      if (!payload.customer_id) {
        try {
          const created = await createCustomer({
            name: payload.name,
            phone: payload.phone || null,
            email: payload.email || null,
            phone2: payload.phone2 || null,
            address: payload.address || null,
            postcode: payload.postcode || null,
          });
          payload = {
            ...payload,
            customer_id: created.id,
            customer_uuid: created.uuid,
          };
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Could not create customer';
          toast.error(`Could not create customer: ${message}`);
          throw err; // abort booking creation
        }
      }

      const res = await apiClient.post<CreateBookingResponse>(
        endpoints.bookings.list,
        payload,
      );
      return res.data;
    },
    onSuccess: async (response) => {
      // Drop the draft so refresh doesn't re-offer the just-submitted form.
      await draftManager.clearDraft();
      // Invalidate likely stale lists.
      await queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // Invalidate all diary sub-keys so the diary view reflects the new
      // booking immediately (diary.today, diary.week, diary.drivers, etc.).
      await queryClient.invalidateQueries({ queryKey: ['diary'] });
      opts.onSubmitSuccess?.(response);
    },
    onError: (err) => {
      // The server's same-day duplicate guard is a warning the form can
      // confirm, not a dead end: ask, then resubmit with confirm_duplicate.
      if (
        axios.isAxiosError(err) &&
        err.response?.status === 409 &&
        (err.response.data as { code?: string } | undefined)?.code === 'DUPLICATE_REGISTRATION'
      ) {
        setDuplicateConfirm({
          message:
            (err.response.data as { message?: string }).message ??
            'This registration is already booked on this date.',
        });
        return;
      }
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not create booking.'
        : 'Could not create booking.';
      toast.error(message);
    },
  });

  // ---- Mutators ----------------------------------------------------------

  const setField = useCallback(
    <K extends keyof BookingFormState>(key: K, value: BookingFormState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const setVehicle = useCallback(
    (index: number, value: VehicleFormValue) => {
      setState((prev) => {
        const next = [...prev.vehicles];
        next[index] = value;
        return { ...prev, vehicles: next };
      });
    },
    [],
  );

  const addVehicle = useCallback(() => {
    setState((prev) => {
      if (prev.vehicles.length >= 3) return prev;
      return { ...prev, vehicles: [...prev.vehicles, { ...EMPTY_VEHICLE }] };
    });
  }, []);

  const removeVehicle = useCallback((index: number) => {
    setState((prev) => {
      if (index === 0 || prev.vehicles.length <= 1) return prev;
      const next = prev.vehicles.filter((_, i) => i !== index);
      // When the count drops back to a single vehicle, the per-vehicle
      // notes input on VehicleForm unmounts. Clear the value so we don't
      // submit a stale entry the user can no longer see or correct.
      if (next.length === 1 && next[0]) {
        next[0] = { ...next[0], notes: '' };
      }
      return { ...prev, vehicles: next };
    });
    setDuplicates((prev) => {
      const out: Record<number, DuplicateBookingMatch | null> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k);
        if (i === index) return;
        out[i > index ? i - 1 : i] = v;
      });
      return out;
    });
  }, []);

  const setCustomer = useCallback((customer: CustomerSummary) => {
    setState((prev) => ({
      ...prev,
      customer: {
        customer,
        newMode: false,
        fields: {
          // Contact fields (phone/address/postcode) and full_name arrive as
          // null via the normalised customer_contact LEFT JOIN — coalesce so
          // the string-typed form fields (and the inputs bound to them) never
          // hold null.
          name: customer.full_name ?? '',
          email: customer.email ?? '',
          phone: customer.phone ?? '',
          phone2: customer.phone2 ?? '',
          address: customer.address ?? '',
          postcode: customer.postcode ?? '',
        },
      },
    }));
  }, []);

  const setNewCustomerMode = useCallback((newMode: boolean) => {
    setState((prev) => ({
      ...prev,
      customer: {
        customer: null,
        newMode,
        fields: newMode
          ? prev.customer.fields
          : { ...prev.customer.fields },
      },
    }));
  }, []);

  const setCustomerField = useCallback(
    <K extends keyof BookingFormState['customer']['fields']>(
      key: K,
      value: BookingFormState['customer']['fields'][K],
    ) => {
      setState((prev) => ({
        ...prev,
        customer: {
          ...prev.customer,
          // typing in the customer panel implies new-customer mode if no
          // linked customer is selected.
          newMode: prev.customer.customer ? prev.customer.newMode : true,
          fields: { ...prev.customer.fields, [key]: value },
        },
      }));
    },
    [],
  );

  const reset = useCallback(() => {
    setState(makeInitialState());
    setDuplicates({});
    duplicateAckRef.current.clear();
    setDuplicateConfirm(null);
    void draftManager.clearDraft();
  }, [draftManager]);

  return {
    state,
    setState,
    setField,
    setVehicle,
    addVehicle,
    removeVehicle,
    setCustomer,
    setNewCustomerMode,
    setCustomerField,
    reset,

    submit: submitMutation.mutate,
    submitAsync: submitMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,

    checkDuplicate,
    duplicateWarnings,
    duplicateModal,
    dismissDuplicateModal,
    clearVehicleRegistration,

    // Server-side same-day duplicate warning (409 DUPLICATE_REGISTRATION).
    duplicateConfirm,
    confirmDuplicateAndSubmit: () => {
      // The 409 listed every duplicate on this submission; staff confirmed
      // exactly the plates currently on the form.
      for (const v of state.vehicles) {
        const r = normReg(v.car_registration);
        if (r.length > 0) duplicateAckRef.current.add(r);
      }
      setDuplicateConfirm(null);
      submitMutation.mutate();
    },
    cancelDuplicateConfirm: () => setDuplicateConfirm(null),

    // Draft auto-resume prompt (kept at the same field names for the page).
    pendingDraftPrompt: draftManager.pendingDraftPrompt,
    acceptDraft: draftManager.acceptDraft,
    rejectDraft: draftManager.rejectDraft,

    // Draft Manager modal API.
    drafts: draftManager.drafts,
    isDirty: draftManager.isDirty,
    isSavingDraft: draftManager.isSaving,
    listDrafts: draftManager.listDrafts,
    loadDraft: draftManager.loadDraft,
    deleteDraft: draftManager.deleteDraft,
    saveDraft: draftManager.saveDraft,
  };
}
